// Centralized GA4 event firing.
// All product-funnel events go through here so we have one place to look,
// one place to mock in tests, and one place to add new sinks (Clarity custom
// tags, server-side mirror, etc.) later.

function isInIndia() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz === 'Asia/Kolkata';
  } catch (_) {
    return false;
  }
}

function getTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (_) {
    return 'unknown';
  }
}

async function hashEmail(email) {
  if (!email || typeof email !== 'string') return undefined;
  try {
    const data = new TextEncoder().encode(email.trim().toLowerCase());
    const buf = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 16);
  } catch (_) {
    return undefined;
  }
}

function fire(eventName, params = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const payload = {
    timezone: getTimezone(),
    is_india: isInIndia(),
    ...params,
  };
  try {
    window.gtag('event', eventName, payload);
    if (typeof window !== 'undefined' && window.localStorage?.getItem('debug_analytics') === '1') {
      console.log(`[gtag] ${eventName}`, payload);
    }
  } catch (e) {
    console.warn('[gtag] failed', eventName, e?.message);
  }
}

export const track = {
  auditStarted: (profileUrl) =>
    fire('audit_started', { profile_url_present: !!profileUrl }),
  questionnaireStarted: () => fire('questionnaire_started'),
  questionnaireCompleted: (answerCount) =>
    fire('questionnaire_completed', { answer_count: answerCount }),
  auditCompleted: (score) =>
    fire('audit_completed', { score: typeof score === 'number' ? score : undefined }),
  authWallShown: () => fire('auth_wall_shown'),
  authSignedIn: async (email) =>
    fire('auth_signed_in', { user_email_hash: await hashEmail(email) }),
  paywallShown: ({ amount, currency, source } = {}) =>
    fire('paywall_shown', { amount, currency, source }),
  paymentInitiated: ({ amount, currency, orderId } = {}) =>
    fire('payment_initiated', { amount, currency, order_id: orderId }),
  paymentSuccess: ({ amount, currency, paymentId, orderId } = {}) =>
    fire('payment_success', {
      amount,
      currency,
      value: amount ? amount / 100 : undefined, // GA4 expects value in major units
      payment_id: paymentId,
      order_id: orderId,
    }),
  paymentFailed: ({ amount, currency, orderId, errorCode, errorDescription } = {}) =>
    fire('payment_failed', {
      amount,
      currency,
      order_id: orderId,
      error_code: errorCode,
      error_description: errorDescription,
    }),
  paymentDismissed: ({ amount, currency, orderId } = {}) =>
    fire('payment_dismissed', { amount, currency, order_id: orderId }),
  pdfDownloaded: ({ source } = {}) => fire('pdf_downloaded', { source }),
};

export const analyticsHelpers = { isInIndia, getTimezone, hashEmail };
